import { Component, inject, signal } from '@angular/core'
import {
  catchError,
  combineLatest,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs'
import {
  ColDef,
  GetRowIdFunc,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { AgGridAngular } from 'ag-grid-angular'
import { TableLoadingOverlayComponent } from './overlays/table-loading-overlay.component'
import { TableNoDataOverlayComponent } from './overlays/table-no-data-overlay.component'
import { TableSortService } from './services/table-sort.service'
import { ActiveFilterListComponent } from './filters/active-filter-list/active-filter-list.component'
import { RLSCertificatesService } from '../rls-certificates/rls-certificates.service'
import { RLSCertMetadata } from 'src/app/shared/models/rls-cert-metadata'
import {
  filterGroup,
  RLSCertsColumnDefinition,
} from './definitions/rls-certs-column-definition'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { RLSCertConfirmDeleteModal } from '../modals/rls-cert-confirm-delete-modal/rls-cert-confirm-delete-modal.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-rls-certs-table',
  imports: [AgGridAngular, ActiveFilterListComponent, AsyncPipe],
  providers: [TableSortService],
  styles: `
      :host {
          display: flex;
          flex-direction: column;
          justify-content: start;
      }
      app-table-pagination {
          margin-top: 1rem;
      }
    `,
  template: `
    <app-active-filter-list
      [filterGroup]="filterGroup"
    ></app-active-filter-list>
    <ag-grid-angular
      class="app-table"
      style="height: calc(100vh - 250px)"
      [getRowId]="getRowId"
      [columnDefs]="columnDefs"
      [rowData]="data$ | async"
      [suppressPaginationPanel]="true"
      [suppressDragLeaveHidesColumns]="true"
      [loadingOverlayComponent]="loadingOverlayComponent"
      [noRowsOverlayComponent]="noRowsOverlayComponent"
      [noRowsOverlayComponentParams]="noRowsOverlayParams()"
      [pagination]="false"
      [enableCellTextSelection]="true"
      (gridReady)="onGridReady($event)"
      [loading]="loading()"
    />
  `,
})
export class RLSCertsTableComponent {
  private readonly modalService = inject(NgbModal)
  private readonly rlsCertificatesService = inject(RLSCertificatesService)
  private readonly sortService = inject(TableSortService)
  private readonly filterService = inject(FilterService)
  private gridApi: GridApi | undefined

  protected readonly loading = signal(false)
  protected readonly noRowsOverlayParams = signal({ error: false })

  // DATA
  protected readonly data$: Observable<RLSCertMetadata[]> = combineLatest([
    this.filterService.asParams(filterGroup),
    this.sortService.getOrder(),
  ]).pipe(
    tap(() => {
      this.hideOverlay()
      this.loading.set(true)
    }),
    switchMap(([filters, sortOrder]) => {
      return this.rlsCertificatesService.getCertMetadata(filters, sortOrder)
    }),
    tap(results => {
      this.loading.set(false)
      if (results.length === 0) {
        this.showNoRowsOverlay(false)
      }
    }),
    catchError(() => {
      this.loading.set(false)
      this.showNoRowsOverlay(true)
      return []
    }),
    shareReplay(1),
  )

  private activeGridApi(): GridApi | undefined {
    if (this.gridApi?.isDestroyed()) {
      return undefined
    } else {
      return this.gridApi
    }
  }

  private showNoRowsOverlay(error: boolean) {
    this.noRowsOverlayParams.set({ error })
    this.activeGridApi()?.showNoRowsOverlay()
  }

  private hideOverlay() {
    this.activeGridApi()?.hideOverlay()
  }

  protected getRowId: GetRowIdFunc<RLSCertMetadata> = (data) =>
    data.data.participant + data.data.expiration_date

  protected onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api
  }

  // columns
  protected loadingOverlayComponent = TableLoadingOverlayComponent
  protected noRowsOverlayComponent = TableNoDataOverlayComponent
  private rlsCertsColumnDefinition = new RLSCertsColumnDefinition([
    {
      name: 'Download',
      onAction: ((d: RLSCertMetadata) => {
        this.rlsCertificatesService.downloadCert(d.participant).subscribe()
      }),
    },
    {
      name: 'Delete',
      onAction: ((d: RLSCertMetadata) => {
        this.modalService
          .open(RLSCertConfirmDeleteModal, { centered: true })
          .componentInstance.init(d)
      }),
    },
  ])
  protected columnDefs: ColDef[] = this.rlsCertsColumnDefinition.getColDefs()
  protected filterGroup = filterGroup
}
